import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, Play, CheckCircle2, AlertCircle, Calendar } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardDescription, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field, Input } from '@/components/Input';
import { StatusPill } from '@/components/StatusPill';
import { api } from '@/lib/api';
import { money, date } from '@/lib/format';
import { toast } from '@/components/Toaster';

interface RunPostedRow { asset_id: string; asset_name: string; amount: string; rows: number; }
interface RunFailedRow { asset_id: string; asset_name: string; error: string; }
interface RunResult {
  as_of: string;
  company_id: string;
  processed: number;
  total_amount: string;
  assets_posted: RunPostedRow[];
  assets_failed: RunFailedRow[];
}

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * Month-end batch — runs depreciation for every eligible asset in the
 * active company up to `as_of`. Per-asset failures are reported (not
 * fatal) so the button remains useful even with one misconfigured asset.
 */
export function DepreciationRun() {
  const qc = useQueryClient();
  const [asOf, setAsOf] = useState<string>(todayISO());
  const [result, setResult] = useState<RunResult | null>(null);

  const runMutation = useMutation({
    mutationFn: () =>
      api<RunResult>(`/assets/depreciation-run?as_of=${encodeURIComponent(asOf)}`, { method: 'POST' }),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`Posted ${r.processed} asset${r.processed === 1 ? '' : 's'}`, money(r.total_amount));
      qc.invalidateQueries({ queryKey: ['doctype', '/assets/assets'] });
      qc.invalidateQueries({ queryKey: ['doctype', '/accounting/journal-entries'] });
    },
    onError: (e: { message?: string }) => toast.error('Run failed', e?.message ?? 'See API logs'),
  });

  return (
    <>
      <PageHeader
        crumbs={[
          { label: 'Asset & Inventory', to: '/assets' },
          { label: 'Depreciation run' },
        ]}
        title="Depreciation run"
        subtitle="Month-end batch. Posts depreciation rows due on or before the as-of date for every eligible asset, one JE per asset."
        actions={
          <Button variant="ghost" asChild>
            <Link to={'/assets' as never}><ArrowLeft className="size-4" /> Back to Assets</Link>
          </Button>
        }
      />

      <div className="flex-1 px-4 sm:px-6 lg:px-8 pt-6 pb-8 max-w-4xl space-y-4">
        <Card>
          <CardTitle>Run parameters</CardTitle>
          <CardDescription>
            Pick a cutoff date. Every submitted asset whose next scheduled depreciation row is on or before this date will post.
          </CardDescription>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 items-end">
            <Field label="As of" hint="UTC date — usually the month-end.">
              <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
            </Field>
            <div>
              <Button onClick={() => runMutation.mutate()} loading={runMutation.isPending} disabled={!asOf}>
                <Play className="size-4" /> Run depreciation
              </Button>
            </div>
          </div>
        </Card>

        {result && (
          <>
            <Card>
              <div className="flex items-center justify-between">
                <CardTitle>Result</CardTitle>
                <StatusPill tone={result.assets_failed.length > 0 ? 'warning' : 'success'} withDot={false}>
                  <Calendar className="size-3" /> {date(result.as_of)}
                </StatusPill>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3 text-body-sm">
                <Stat label="Processed" value={String(result.processed)} />
                <Stat label="Total amount" value={money(result.total_amount)} mono />
                <Stat label="Failures" value={String(result.assets_failed.length)} />
              </div>
            </Card>

            {result.assets_posted.length > 0 && (
              <Card padded={false}>
                <div className="px-5 py-3 border-b border-hairline flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-brand-green" />
                  <CardTitle>Posted ({result.assets_posted.length})</CardTitle>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-body-sm">
                    <thead className="bg-surface-soft text-micro-uppercase text-stone">
                      <tr>
                        <th className="text-left font-medium px-4 py-2">Asset</th>
                        <th className="text-right font-medium px-4 py-2">Rows</th>
                        <th className="text-right font-medium px-4 py-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.assets_posted.map((r) => (
                        <tr key={r.asset_id} className="border-t border-hairline">
                          <td className="px-4 py-2">
                            <Link to={`/assets/assets/${r.asset_id}` as never} className="text-brand-green-ink hover:underline">{r.asset_name}</Link>
                          </td>
                          <td className="px-4 py-2 text-right num text-stone">{r.rows}</td>
                          <td className="px-4 py-2 text-right num">{money(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {result.assets_failed.length > 0 && (
              <Card padded={false}>
                <div className="px-5 py-3 border-b border-hairline flex items-center gap-2">
                  <AlertCircle className="size-4 text-warning" />
                  <CardTitle>Failures ({result.assets_failed.length})</CardTitle>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-body-sm">
                    <thead className="bg-surface-soft text-micro-uppercase text-stone">
                      <tr>
                        <th className="text-left font-medium px-4 py-2">Asset</th>
                        <th className="text-left font-medium px-4 py-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.assets_failed.map((r) => (
                        <tr key={r.asset_id} className="border-t border-hairline">
                          <td className="px-4 py-2">
                            <Link to={`/assets/assets/${r.asset_id}` as never} className="text-brand-green-ink hover:underline">{r.asset_name}</Link>
                          </td>
                          <td className="px-4 py-2 text-brand-error">{r.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-caption text-stone uppercase tracking-wide">{label}</div>
      <div className={'text-section-head ' + (mono ? 'num' : '')}>{value}</div>
    </div>
  );
}
